import { Cpu, Sigma } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateWireMaterial } from "./formulas";
import { MaterialDropdown } from "./MaterialDropdown";
import { materialDensityPresets } from "./presets";
import type { WireMaterialInputs } from "./types";

type DepositionCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  inputs: WireMaterialInputs;
  inputText: Record<keyof WireMaterialInputs, string>;
  onInputTextChange: (key: keyof WireMaterialInputs, value: string) => void;
};

function displayMassKg(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 2.2046226218;
}

export function DepositionCalculator({
  language,
  unitSystem,
  inputs,
  inputText,
  onInputTextChange
}: DepositionCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateWireMaterial(inputs), [inputs]);
  const depositionUnit = unitSystem === "metric" ? "kg/h" : "lb/h";
  const selectedMaterial = materialDensityPresets.find((preset) => Math.abs(preset.density - inputs.density) < 0.005);

  return (
    <section className="calculator-panel wire-material-panel" aria-label={t.modules.deposition}>
      <div className="calculator-heading">
        <div>
          <h2>{t.modules.deposition}</h2>
          <span>{t.modules.depositionDescription}</span>
        </div>
      </div>

      <div className="calculator-body wire-module-body">
        <div className="parameter-section wire-module-parameters">
          <div className="section-label">{t.inputParameters}</div>
          <div className="preset-panel wire-module-presets">
            <div className="section-label">{t.wireMaterial.material}</div>
            <MaterialDropdown
              label={t.wireMaterial.material}
              customLabel={t.wireMaterial.customMaterial}
              values={t.wireMaterial.materials}
              selectedKey={selectedMaterial?.key}
              onSelect={(density) => onInputTextChange("density", String(density))}
            />
          </div>
          <div className="input-grid">
            <NumericInput
              label={t.inputs.wireDiameter}
              shortLabel="Draht"
              unit={unitFor("wireDiameter", unitSystem)}
              value={toDisplayValue("wireDiameter", inputs.wireDiameter, unitSystem)}
              textValue={inputText.wireDiameter}
              decimals={decimalsFor("wireDiameter", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("wireDiameter", value)}
            />
            <NumericInput
              label={t.wireMaterial.density}
              shortLabel={t.wireMaterial.densityShort}
              unit="g/cm³"
              value={inputs.density}
              textValue={inputText.density}
              decimals={2}
              locale={locale}
              onChange={(value) => onInputTextChange("density", value)}
            />
            <NumericInput
              label={t.inputs.wireFeed}
              shortLabel="vD"
              unit={unitFor("wireFeed", unitSystem)}
              value={toDisplayValue("wireFeed", inputs.wireFeed, unitSystem)}
              textValue={inputText.wireFeed}
              decimals={decimalsFor("wireFeed", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("wireFeed", value)}
            />
          </div>
        </div>

        <aside className="result-panel wire-module-results" aria-live="polite">
          <div className="result-header">
            <span>
              <Cpu size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{t.wireMaterial.depositionRate}</p>
          <div className="result-value">
            {formatNumber(displayMassKg(result.depositionRate, unitSystem), 2, locale)}
            <span>{depositionUnit}</span>
          </div>

          <div className="formula-box">
            <span>
              <Sigma size={15} /> {t.formula}
            </span>
            <code>A(Draht) × ρ × vD × 60 / 1000</code>
          </div>

          <div className="detail-grid">
            <div>
              <span>{t.labels.wireArea}</span>
              <strong>{formatNumber(result.wireArea, 3, locale)} mm²</strong>
            </div>
            <div>
              <span>{t.wireMaterial.weightPerMeter}</span>
              <strong>{formatNumber(result.weightPerMeter, 2, locale)} g/m</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
